import React from 'react'
import { Image, ImageSourcePropType, StyleSheet } from 'react-native'
import { ContentVariant } from 'src/components/DNA/types'
import {
  Iffy,
  DNACard,
  DNABox,
  DNAText,
  DNAIconStyleProps,
  DNAIcon,
  util,
  DNAButton,
  colors,
} from '@alucio/lux-ui'
import { useHistory } from 'src/router'
import { useDNAFolderActions } from 'src/components/DNA/Folder/DNAFolder.actions'
import { useAppSettings } from 'src/state/context/AppSettings'
import { Dimensions } from '@alucio/core'
import FolderEmptyLabel from 'src/components/DNA/Empty/FolderEmptyLabel'
import OpenedPresentationsEmptyState from 'src/components/DNA/Empty/OpenedPresentationsEmptyState'
import HubEmptyState from 'src/components/DNA/Empty/HubsEmptyState'
import HubsTemplateEmptyState from 'src/components/DNA/Empty/HubsTemplateEmptyState'
import BeaconEmptyStateImage from 'src/assets/images/BeaconEmptyState'
import { FolderORM } from 'src/types/types'
import SvgFavoritesEmpty from 'src/assets/images/FavoritesEmpty'

const LoaderImg = require('../../../../assets/images/LoaderDNA.gif')
const LoaderImgDark = require('../../../../assets/images/LoaderDNA_dark.gif')

const S = StyleSheet.create({
  labelText: {
    fontSize: 15,
    color: colors['color-ui2-neutral-500'],
    fontWeight: '600',
  },
  labelAndActionButtonContainer: {
    paddingBottom: 30,
  },
})

export enum EmptyVariant {
  // Document
  DocumentGridBookmark = 'DocumentGridBookmark',
  DocumentGridRecentlyUpdated = 'DocumentGridRecentlyUpdated',
  DocumentGridEmpty = 'DocumentGridEmpty',
  DocumentGridEmptySearch = 'DocumentGridEmptySearch',
  DocumentListEmpty = 'DocumentListEmpty',
  DocumentListEmptySearch = 'DocumentListEmptySearch',
  DocumentDashboardEmpty = 'DocumentDashboardEmpty',
  DocumentFavoriteDashboardEmpty = 'DocumentFavoriteDashboardEmpty',
  FolderFavoriteDashboardEmpty = 'FolderFavoriteDashboardEmpty',
  // Email Templates
  EmailTemplatesListEmpty = 'EmailTemplatesListEmpty',
  // Search Results
  DocumentResultsListEmpty = 'DocumentResultsListEmpty',
  DocumentResultsGridEmpty = 'DocumentResultsGridEmpty',
  MeetingsDocumentResultsEmpty = 'MeetingsDocumentResultsEmpty',
  // Publisher Search Results
  PublisherDocumentResultsListEmpty = 'PublisherDocumentResultsListEmpty',
  // Folder
  FolderListBookmark = 'FolderListBookmark',
  FolderListCreate = 'FolderListCreate',
  FolderSharedList = 'FolderSharedList',
  FolderGridEmpty = 'FolderGridEmpty',
  FolderListEmpty = 'FolderListEmpty',
  FolderListEmptyWithNoInteraction = 'FolderListEmptyWithNoInteraction',
  FolderUpdatesEmpty = 'FolderUpdatesEmpty',
  FolderListLoading = 'FolderListLoading',
  FolderListLoadingDark = 'FolderListLoadingDark',
  FolderListSharedOffline = 'FolderListSharedOffline',
  FolderListPresentEmpty = 'FolderListPresentEmpty',
  FolderSharedPresentEmpty = 'FolderSharedPresentEmpty',
  FileListPresentEmpty = 'FileListPresentEmpty',
  FolderBookmarkPresentEmpty = 'FolderBookmarkPresentEmpty',
  ArchivedFolderEmpty = 'ArchivedFolderEmpty',
  UploadsEmpty = 'UploadsEmpty',
  FolderDashBoardEmpty = 'FolderDashBoardEmpty',
  // Meetings History
  MeetingsHistoryEmpty = 'MeetingsHistoryEmpty',
  NoNetworkMeetingsHistory = 'NoNetworkMeetingsHistory',
  // Meetings Opened Presentation List
  MeetingsOpenedPresentationsEmpty = 'MeetingsOpenedPresentationsEmpty',
  // Hubs
  HubsEmpty = 'HubsEmpty',
  HubsTemplateEmpty = 'HubsTemplateEmpty',
  // Users List
  UsersListEmpty = 'UsersListEmpty',
  // Reports
  AnalyticsEmpty = 'AnalyticsEmpty',
  AnalyticsLoading = 'AnalyticsLoading',
  // Offline
  NoNetworkGenericUnavailable = 'NoNetworkGenericUnavailable',
  // Search
  SearchInstructions = 'SearchInstructions',
  // Integrations
  IntegrationSettingsEmpty = 'IntegrationSettingsEmpty',
  IntegrationPublisherSettingsEmpty = 'IntegrationPublisherSettingsEmpty',
  // Custom Deck - Find Replacement
  FindReplacementEmpty = 'FindReplacementEmpty',
  // My Updates
  MyUpdatesEmpty = 'MyUpdatesEmpty',
  // Custom Label Variant
  CustomLabelEmpty = 'CustomLabelEmpty',
  // No Folders Available
  NoFoldersAvailable = 'NoFoldersAvailable',
  NoFavoriteFoldersAvailable = 'NoFavoriteFoldersAvailable',
  NoUploadsAvailable = 'NoUploadsAvailable',
  // Custom Empty Component
  CustomEmptyComponent = 'CustomEmptyComponent',
}

interface VariantOptsProps {
  image?: typeof BeaconEmptyStateImage | ImageSourcePropType,
  icon?: DNAIconStyleProps,
  imageDimension?: Dimensions,
  label: string | React.ReactElement,
  actionLabel?: string,
  customEmptyComponent?: React.ReactElement | undefined,
  Container?: any,
  testPrefix?: string,
  onPress?: (
    history: ReturnType<typeof useHistory>,
    folderActions?: ReturnType<typeof useDNAFolderActions>
  ) => void
}

type VariantOpts = Record<EmptyVariant, VariantOptsProps>

const variantOpts: VariantOpts = {
  // Document
  [EmptyVariant.DocumentGridBookmark]: {
    image: BeaconEmptyStateImage,
    label: 'Start favoriting content to find them here',
    actionLabel: 'Start favoriting',
    onPress: (history) => history.push('/library'),
  },
  [EmptyVariant.DocumentGridRecentlyUpdated]: {
    image: BeaconEmptyStateImage,
    label: 'No recently updated files to review',
  },
  [EmptyVariant.DocumentGridEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'No results available for current filters',
  },
  [EmptyVariant.DocumentGridEmptySearch]: {
    image: BeaconEmptyStateImage,
    label: 'No results available for current filters',
  },
  [EmptyVariant.DocumentListEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'No results match your filters. Try adjusting them to see more.',
  },
  [EmptyVariant.DocumentListEmptySearch]: {
    image: BeaconEmptyStateImage,
    label: 'No results found. Please try a different search.',
    Container: ({ children, style }) => (
      <DNABox fill style={style}>
        {children}
      </DNABox>
    ),
  },
  [EmptyVariant.DocumentDashboardEmpty]: {
    label: 'No documents available',
    actionLabel: 'View library',
    onPress: (history) => history.push('/library'),
  },
  [EmptyVariant.DocumentFavoriteDashboardEmpty]: {
    image: SvgFavoritesEmpty,
    label: 'Start favoriting content to see them here',
    actionLabel: 'View library',
    onPress: (history) => history.push('/library'),
    imageDimension: { height: 280, width: 320 },
  },
  [EmptyVariant.FolderFavoriteDashboardEmpty]: {
    image: SvgFavoritesEmpty,
    label: 'Start favoriting folders to see them here',
    actionLabel: 'View folders',
    onPress: (history) => history.push('/folders'),
    imageDimension: { height: 216, width: 230 },
  },
  // Email Templates
  [EmptyVariant.EmailTemplatesListEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'There are currently no templates.',
  },
  // Search Results
  [EmptyVariant.DocumentResultsListEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'No results found. Please try a different search.',
    Container: ({ children, style }) => (
      <DNABox fill style={style}>
        {children}
      </DNABox>
    ),
  },
  [EmptyVariant.DocumentResultsGridEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'No results found. Please try a different search.',
  },
  [EmptyVariant.MeetingsDocumentResultsEmpty]: {
    label: <DNAText colorMode="dark">No results found. Please try a different search.</DNAText>,
  },
  // Publisher Search Results
  [EmptyVariant.PublisherDocumentResultsListEmpty]: {
    label: 'No results found. Please try a different search.',
    Container: ({ children, style }) => (
      <DNABox fill style={[...style, { height: 400 }]}>
        {children}
      </DNABox>
    ),
  },
  // Folder
  [EmptyVariant.FolderListBookmark]: {
    image: BeaconEmptyStateImage,
    label: 'No favorites available',
    actionLabel: 'View folders',
    onPress: (history) => history.push('/folders'),
  },
  [EmptyVariant.FolderListCreate]: {
    image: BeaconEmptyStateImage,
    label: 'Create folders to organize your files',
    actionLabel: 'Create folder',
    testPrefix: 'my-folders',
    onPress: (history, folderActions) => {
      // Cheating because this action doesn't require a FolderORM
      folderActions?.createFolder?.(history as any as FolderORM)?.()
    },
  },
  [EmptyVariant.FolderSharedList]: {
    image: BeaconEmptyStateImage,
    label: 'Folders shared with you will be listed here',
  },
  [EmptyVariant.FolderGridEmpty]: {
    label: '',
    customEmptyComponent: <FolderEmptyLabel />,
  },
  [EmptyVariant.FolderListEmpty]: {
    label: '',
    customEmptyComponent: <FolderEmptyLabel />,
  },
  [EmptyVariant.FolderListEmptyWithNoInteraction]: {
    image: BeaconEmptyStateImage,
    label: 'No files available in this folder',
  },
  [EmptyVariant.FolderUpdatesEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'No updates outstanding',
  },
  [EmptyVariant.FolderListLoading]: {
    image: LoaderImg,
    imageDimension: {
      height: 120,
      width: 120,
    },
    label: 'Loading folders...',
  },
  [EmptyVariant.FolderListLoadingDark]: {
    image: LoaderImgDark,
    imageDimension: {
      height: 120,
      width: 120,
    },
    label: <DNAText testID="loading-folders" h5 colorMode="dark">Loading folders...</DNAText>,
  },
  [EmptyVariant.FolderListSharedOffline]: {
    image: BeaconEmptyStateImage,
    imageDimension: {
      width: 60,
      height: 73,
    },
    label: 'Shared folders are unavailable in offline mode.',
  },
  [EmptyVariant.FolderListPresentEmpty]: {
    label: (<DNAText bold colorMode="dark">
      No folders available
    </DNAText>),
  },
  [EmptyVariant.FolderSharedPresentEmpty]: {
    label: (<DNAText bold colorMode="dark">
      Folders shared with you will be listed here.
    </DNAText>),
  },
  [EmptyVariant.FileListPresentEmpty]: {
    label: (<DNAText bold colorMode="dark">
      No files available in this folder
    </DNAText>),
  },
  [EmptyVariant.FolderBookmarkPresentEmpty]: {
    label: (<DNAText bold colorMode="dark">
      No favorites available.
    </DNAText>),
  },
  [EmptyVariant.ArchivedFolderEmpty]: {
    label: 'Archive old folders to reduce clutter.',
  },
  [EmptyVariant.UploadsEmpty]: {
    label: 'Upload content to use in your presentations.',
    actionLabel: 'Upload',
  },
  [EmptyVariant.FolderDashBoardEmpty]: {
    label: 'Access folders to see them listed here',
    actionLabel: 'View folders',
    onPress: (history) => history.push('/folders'),
  },
  // Meetings History
  [EmptyVariant.MeetingsHistoryEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'Host or create meetings to find them here',
  },
  [EmptyVariant.NoNetworkMeetingsHistory]: {
    image: BeaconEmptyStateImage,
    imageDimension: {
      width: 60,
      height: 73,
    },
    label: 'Meeting history is unavailable in offline mode.',
  },
  // Meetings Opened Presentation List
  [EmptyVariant.MeetingsOpenedPresentationsEmpty]: {
    label: '',
    Container: () => (
      <DNABox fill appearance="col">
        <OpenedPresentationsEmptyState />
      </DNABox>),
  },
  // Hubs
  [EmptyVariant.HubsEmpty]: {
    label: '',
    Container: () => (
      <DNABox fill childFill appearance="col">
        <HubEmptyState />
      </DNABox>),
  },
  [EmptyVariant.HubsTemplateEmpty]: {
    label: '',
    Container: () => (
      <DNABox fill childFill appearance="col">
        <HubsTemplateEmptyState />
      </DNABox>),
  },
  // Users List
  [EmptyVariant.UsersListEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'Add users to get started',
  },
  // Reports
  [EmptyVariant.AnalyticsEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'Select a report to see it here',
  },
  [EmptyVariant.AnalyticsLoading]: {
    image: LoaderImg,
    label: 'Loading reports...',
  },
  // Offline
  [EmptyVariant.NoNetworkGenericUnavailable]: {
    icon: {
      status: 'warning',
      name: 'wifi-slash',
      appearance: 'ghost',
      size: 'xl',
    },
    label: 'Unavailable offline',
  },
  // Search
  [EmptyVariant.SearchInstructions]: {
    icon: {
      status: 'primary',
      appearance: 'ghost',
      name: 'magnifying-glass',
      size: 'xl',
    },
    label: 'Enter a search term to begin',
  },
  // Integrations
  [EmptyVariant.IntegrationSettingsEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'Add a new integration to get started',
    actionLabel: 'Add integration',
  },
  [EmptyVariant.IntegrationPublisherSettingsEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'Currently, there are no integrations available for this tenant.',
    actionLabel: '',
  },
  // Custom Deck - Find Replacement
  [EmptyVariant.FindReplacementEmpty]: {
    image: BeaconEmptyStateImage,
    label: (<DNAText bold status="subtle">
      No slides remaining.
    </DNAText>),
  },
  [EmptyVariant.MyUpdatesEmpty]: {
    label: 'You’re all caught up!',
  },
  [EmptyVariant.CustomLabelEmpty]: {
    image: BeaconEmptyStateImage,
    label: 'This is a custom label for the empty state',
    actionLabel: 'Take action',
    onPress: (history) => history.push('/library'),
  },
  [EmptyVariant.NoFoldersAvailable]: {
    label: 'No folders available.',
  },
  [EmptyVariant.NoFavoriteFoldersAvailable]: {
    image: BeaconEmptyStateImage,
    label: 'No favorite folders available.',
    actionLabel: 'View folders',
    onPress: (history) => history.push('/folders'),
  },
  [EmptyVariant.NoUploadsAvailable]: {
    label: 'No uploads available.',
  },
  [EmptyVariant.CustomEmptyComponent]: {
    label: '',
  },
}

export interface CustomEmptyStateProps {
  customEmptyLabel?: string | React.ReactElement,
  customEmptyActionLabel?: string,
  customEmptyOnPress?: (history: ReturnType<typeof useHistory>) => void,
  customEmptyButton?: React.ReactElement,
  customEmptyComponent?: React.ReactElement,
}

// [TODO] - Move to lux-ui?
export interface EmptyStateProps extends CustomEmptyStateProps {
  emptyVariant: EmptyVariant,
  variant?: ContentVariant,
  useContainer?: boolean,
}

export const DNAEmpty: React.FC<EmptyStateProps> = (props) => {
  const { deviceMode } = useAppSettings()
  const {
    emptyVariant,
    variant = 'default',
    useContainer = true,
    customEmptyLabel,
  } = props
  const {
    Container,
    image,
    imageDimension,
    label: defaultLabel,
    icon,
  } = variantOpts[emptyVariant]

  const label = emptyVariant === EmptyVariant.CustomLabelEmpty && customEmptyLabel
    ? customEmptyLabel
    : defaultLabel

  // Some variants are wrapped in Cards
  const ConditionalContainer = useContainer
    ? Container || DNACard
    : React.Fragment

  const isVirtual = variant === 'virtual'
  const isMeetings = variant === 'meetings'

  // this flag is used so that the white background of the main dnaEmpty box does not overlap the border of the lists
  const desktopBorder = emptyVariant === EmptyVariant.FolderListCreate ||
    emptyVariant === EmptyVariant.UploadsEmpty ||
    emptyVariant === EmptyVariant.FolderListEmpty ||
    emptyVariant === EmptyVariant.MeetingsOpenedPresentationsEmpty ||
    emptyVariant === EmptyVariant.FindReplacementEmpty

  const isCustomDeck = variant === 'customDeck'
  const bgColor = {
    backgroundColor: isMeetings || isVirtual || deviceMode === 'tablet' || desktopBorder
      ? 'transparent' : 'white',
  }
  const conditionalProps = useContainer
    ? { style: [{ shadowOpacity: 0 }, bgColor] }
    : {}

  return (
    <DNABox
      fill
      alignY={emptyVariant !== EmptyVariant.MeetingsOpenedPresentationsEmpty ? 'center' : 'start'}
      style={bgColor}
    >
      <ConditionalContainer {...conditionalProps}>
        <DNABox
          fill
          appearance="col"
          alignX="center"
          alignY="center"
          spacing="md"
          style={[{ padding: 48 }, bgColor]}
        >
          <Iffy is={(!isVirtual && !isCustomDeck && image)}>
            {
              typeof image !== 'function'
                ? (
                  <Image
                    source={image!}
                    style={util.mergeStyles(
                      undefined,
                      [{ height: 205, width: 341 }, !imageDimension],
                      [{ height: imageDimension?.height, width: imageDimension?.width }, !!imageDimension],
                    )}
                    resizeMode="contain"
                  />
                )
                : (
                  <BeaconEmptyStateImage
                    height={imageDimension?.height || 320}
                    width={imageDimension?.width || 320}
                  />
                )
            }
          </Iffy>

          <Iffy is={icon}>
            <DNAIcon.Styled {...icon} />
          </Iffy>

          <DNAText
            h5
            testID="empty-content-placeholder"
            {...((isVirtual || isCustomDeck || isMeetings) ? { status: 'subtle' } : {})}
          >
            {label}
          </DNAText>
        </DNABox>
      </ConditionalContainer>
    </DNABox>
  )
}

export const DNAEmptyV2: React.FC<EmptyStateProps> = (props) => {
  const {
    emptyVariant, customEmptyLabel, customEmptyActionLabel, customEmptyOnPress,
    customEmptyButton, customEmptyComponent,
  } = props;
  const {
    imageDimension,
    label: defaultLabel,
    actionLabel: defaultActionLabel,
    onPress: defaultOnPress,
    customEmptyComponent: defaultCustomEmptyComponent,
  } = variantOpts[emptyVariant];
  const label = customEmptyLabel || defaultLabel
  const actionLabel =  customEmptyActionLabel || (emptyVariant === EmptyVariant.CustomLabelEmpty
    ? undefined
    : defaultActionLabel)
  const onPress = customEmptyOnPress || (emptyVariant === EmptyVariant.CustomLabelEmpty
    ? undefined
    : defaultOnPress)

  const history = useHistory();
  const folderActions = useDNAFolderActions();
  const displayActionbutton = actionLabel && onPress;

  if (customEmptyComponent || defaultCustomEmptyComponent) {
    return customEmptyComponent || defaultCustomEmptyComponent || null
  }

  return (
    <DNABox fill alignY="center" alignX="center" appearance="col">
      {/* IMAGE */}
      <BeaconEmptyStateImage
        height={imageDimension?.height || 320}
        width={imageDimension?.width || 320}
      />
      {/* LABEL & ACTION BUTTON */}
      <DNABox appearance="col" alignX="center" spacing="md" style={S.labelAndActionButtonContainer}>
        <DNAText testID="empty-content-placeholder" style={S.labelText}>{label}</DNAText>
        {customEmptyButton || (displayActionbutton && (
          <DNAButton
            onPress={() => onPress(history, folderActions)}
            children={actionLabel}
          />
        ))}
      </DNABox>
    </DNABox>
  )
}

const DNAEmptyV2Variants = [
  EmptyVariant.DocumentDashboardEmpty,
  EmptyVariant.DocumentFavoriteDashboardEmpty,
  EmptyVariant.FolderFavoriteDashboardEmpty,
  EmptyVariant.FolderDashBoardEmpty,
  EmptyVariant.CustomLabelEmpty,
  EmptyVariant.FolderListCreate,
  EmptyVariant.FolderSharedList,
  EmptyVariant.UploadsEmpty,
  EmptyVariant.MyUpdatesEmpty,
  EmptyVariant.ArchivedFolderEmpty,
  EmptyVariant.DocumentGridBookmark,
  EmptyVariant.DocumentListEmptySearch,
  EmptyVariant.MeetingsHistoryEmpty,
  EmptyVariant.CustomEmptyComponent,
  EmptyVariant.FolderGridEmpty,
  EmptyVariant.IntegrationSettingsEmpty,
  EmptyVariant.IntegrationPublisherSettingsEmpty,
  EmptyVariant.FolderListEmpty,
  EmptyVariant.NoFavoriteFoldersAvailable,
  EmptyVariant.FolderListBookmark,
]

export const DNAEmptyWrapper: React.FC<EmptyStateProps> = (props) => {
  if (DNAEmptyV2Variants.includes(props.emptyVariant)) return <DNAEmptyV2 {...props} />
  else return <DNAEmpty {...props} />
}

export default DNAEmptyWrapper;
